'use client';

type ToolEventPayload = {
  guestSessionId: string | null;
  toolId: string;
  toolName: string;
  toolCategory: string;
  action: string;
  inputLabel?: string;
  outputFormat?: string;
  fileSize?: string;
};

type HistoryPayload = {
  toolId: string;
  toolName: string;
  toolCategory: string;
  toolGradient: string;
  toolIcon: string;
  inputLabel: string;
  outputFormat: string;
  fileSize: string;
  thumbnailColor: string;
  storedFileId: string | null;
};

type OfflineSyncItem =
  | {
      id: string;
      type: 'tool-event';
      createdAt: number;
      payload: ToolEventPayload;
    }
  | {
      id: string;
      type: 'history';
      createdAt: number;
      payload: HistoryPayload;
    };

const OFFLINE_SYNC_QUEUE_KEY = '2xora_offline_sync_queue_v1';

let flushInFlight: Promise<void> | null = null;

function emitSyncStatus(state: 'syncing' | 'synced', remaining: number) {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent('2xora-sync-status', {
      detail: {
        state,
        remaining,
      },
    }),
  );
}

function makeId(prefix: string) {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function readQueue() {
  if (typeof window === 'undefined') {
    return [] as OfflineSyncItem[];
  }

  try {
    const raw = window.localStorage.getItem(OFFLINE_SYNC_QUEUE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as OfflineSyncItem[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: OfflineSyncItem[]) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(OFFLINE_SYNC_QUEUE_KEY, JSON.stringify(queue));
}

function enqueue(item: OfflineSyncItem) {
  const queue = readQueue();
  queue.push(item);
  writeQueue(queue);
}

async function postJson(url: string, body: unknown) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    keepalive: true,
    cache: 'no-store',
  });

  return response.ok;
}

async function syncItem(item: OfflineSyncItem) {
  if (item.type === 'tool-event') {
    return postJson('/api/tool-events', item.payload);
  }

  return postJson('/api/history', item.payload);
}

export async function persistToolEventNowOrQueue(payload: ToolEventPayload) {
  if (typeof window === 'undefined') {
    return;
  }

  if (!navigator.onLine) {
    enqueue({
      id: makeId('tool-event'),
      type: 'tool-event',
      createdAt: Date.now(),
      payload,
    });
    return;
  }

  try {
    const ok = await postJson('/api/tool-events', payload);
    if (!ok) {
      enqueue({
        id: makeId('tool-event'),
        type: 'tool-event',
        createdAt: Date.now(),
        payload,
      });
    }
  } catch {
    enqueue({
      id: makeId('tool-event'),
      type: 'tool-event',
      createdAt: Date.now(),
      payload,
    });
  }
}

export async function persistHistoryNowOrQueue(payload: HistoryPayload) {
  if (typeof window === 'undefined') {
    return;
  }

  if (!navigator.onLine) {
    enqueue({
      id: makeId('history'),
      type: 'history',
      createdAt: Date.now(),
      payload,
    });
    return;
  }

  try {
    const ok = await postJson('/api/history', payload);
    if (!ok) {
      enqueue({
        id: makeId('history'),
        type: 'history',
        createdAt: Date.now(),
        payload,
      });
    }
  } catch {
    enqueue({
      id: makeId('history'),
      type: 'history',
      createdAt: Date.now(),
      payload,
    });
  }
}

export async function flushOfflineSyncQueue() {
  if (typeof window === 'undefined' || !navigator.onLine) {
    return;
  }

  if (flushInFlight) {
    return flushInFlight;
  }

  flushInFlight = (async () => {
    const queue = readQueue();

    if (!queue.length) {
      return;
    }

    emitSyncStatus('syncing', queue.length);

    const remaining: OfflineSyncItem[] = [];

    for (const item of queue) {
      try {
        const ok = await syncItem(item);
        if (!ok) {
          remaining.push(item);
        }
      } catch {
        remaining.push(item);
      }
    }

    writeQueue(remaining);
    emitSyncStatus('synced', remaining.length);
  })();

  try {
    await flushInFlight;
  } finally {
    flushInFlight = null;
  }
}

export function getOfflineSyncQueueSize() {
  return readQueue().length;
}
